import {
  PeerParticipantsWith__Type,
  PeerTeamWith__Type,
  useShowConsumerCampaignById,
  useShowConsumerCampaignSettingsById,
} from '@givebacks/api/store';
import { centsToDollar, numberToThousandsString } from 'core/Utils';
import { DateTime } from 'luxon';
import { Card, Flex, Grid, Text, cx } from 'ui';
import { IconClock, IconGoal, IconProfile, IconUsers } from 'ui/Icons';

import { useShowCurrentCampaign } from '../../../api/new/campaigns/get';
import { useShowCampaignPeer } from '../../../api/new/campaigns/peers/get';
import { PeerTypes } from '../../../api/new/types';
import { CampaignStoreItemTypes } from '../../../api/new/types/utils';
import { CrowdFundingAvatar } from '../CrowdFundingAvatar/CrowdFundingAvatar';
import { CampaignAmountRaised } from '../InfoIcons/CampaignAmountRaised';
import classes from './CampaignCard/CampaignCard.module.css';

const showPeerGoal = (
  campaign?: CampaignStoreItemTypes,
  peer?: PeerTeamWith__Type | PeerParticipantsWith__Type
) => {
  if (!campaign || !peer) return false;
  const isAthon = campaign?.__type === 'athon';
  const teamGoalRequested = campaign?.team_options?.registration_options?.goal?.requested;
  const noGoalSet = Number(peer?.goal) === 0;
  const campaignParticipantGoalSetting = campaign?.participant_options.goal_setting;
  const campaignParticipantGoalType = campaign?.participant_options.goal_type;
  if (peer.__type === 'teams') {
    if (!teamGoalRequested || noGoalSet) {
      return false;
    }
  } else {
    if (campaignParticipantGoalSetting === 'no_goal' || noGoalSet) return false;
    if (isAthon && campaignParticipantGoalType === 'units') return false;
  }
  return true;
};

const TeamParticipants = ({ number }: { number?: number }) => {
  if (!number) return null;
  const formattedNumber = numberToThousandsString(number);

  return (
    <Flex gap="xs" align="center">
      <IconUsers size="16px" color="#888888" />
      <Text size="xs">{formattedNumber || 0}</Text>
    </Flex>
  );
};

const Backers = ({ number }: { number?: number }) => {
  if (!number) return null;
  const formattedNumber = numberToThousandsString(number);
  return (
    <Flex gap="xs" align="center">
      <IconProfile size="16px" color="#888888" />
      <Text size="xs">{formattedNumber || 0}</Text>
    </Flex>
  );
};
const AthonGoalProgress = ({
  activity,
  goal,
  campaign,
  peerType,
}: {
  activity?: number;
  goal: number;
  campaign?: CampaignStoreItemTypes;
  peerType: PeerTypes;
}) => {
  if (!goal) return null;
  if (campaign?.__type !== 'athon') return null;
  const activityGoalString = `${activity} / ${goal} ${campaign.units_of_activity} goal`;
  const currencyGoalString = `${centsToDollar(goal, true)} goal`;

  const isShowParticipantActivityGoal =
    peerType === 'participants' && campaign.participant_options.goal_type === 'units';

  return (
    <Flex gap="xs" align="center">
      <IconGoal size="16px" color="#888888" />
      <Text size="xs">
        {isShowParticipantActivityGoal ? activityGoalString : currencyGoalString}
      </Text>
    </Flex>
  );
};

export type ParticipantTeamProps = {
  type: PeerTypes;
  peer_id?: string;
  onClick?: () => void;
  active?: boolean;
  causeId?: string;
  campaignId?: string;
  isLoading?: boolean;
  shouldShowAmountRaised?: boolean;
  shouldShowGoal?: boolean;
  shouldShowBackers?: boolean;
  heightAuto?: boolean;

  // @bkchu - The following props allow for a quick fix and are
  // safe since I gave them default values that are backwards compatible
  // NOTE: this whole component needs to implement a new generic UI component
  showCampaignName?: boolean;
  showTeamName?: boolean;
  showCampaignTimeLeft?: boolean;
  wrapDescription?: boolean;
};

function CampaignPeerInfoCard({
  peer_id,
  type,
  onClick,
  active = true,
  causeId,
  campaignId,
  shouldShowAmountRaised = true,
  shouldShowGoal = true,
  shouldShowBackers = true,
  heightAuto = false,
  showCampaignName = false,
  showTeamName = true,
  showCampaignTimeLeft = false,
  wrapDescription = true,
}: ParticipantTeamProps) {
  const { data: peer } = useShowCampaignPeer({
    peerType: type,
    participantId: peer_id,
    causeId,
    campaignId,
    queryOptions: {
      options: {
        credits: true,
      },
    },
  });

  const { data: currCampaign } = useShowCurrentCampaign();
  const { data: campaignById } = useShowConsumerCampaignById({ campaignId, causeId });
  const campaign = campaignById ?? currCampaign;

  const { data: settings } = useShowConsumerCampaignSettingsById({ causeId, campaignId });

  const isPeerGoalVisible = showPeerGoal(campaign, peer);

  const dataFields = {
    name: peer?.__type == 'participants' ? peer?.first_name + ' ' + peer?.last_name : peer?.name,
  };

  const daysLeft = settings?.isCampaignReadOnly
    ? 0
    : campaign?.expires_at
      ? Math.ceil(DateTime.fromISO(campaign.expires_at).diffNow('days').days)
      : null;

  if (!peer) return null;

  return (
    <Card
      w="100%"
      radius="md"
      p={0}
      className={classes.campaign_card}
      onClick={onClick}
      withBorder
      active={active}
      component="button"
      mod={{ ['height-auto']: heightAuto }}
    >
      <Flex className={classes.campaign_card_content} rowGap="md" align={'center'} p="md">
        <CrowdFundingAvatar
          isLoading={false}
          img={'image' in peer ? peer?.image?.url : undefined}
          name={dataFields.name}
          place={peer?.rank}
          peerType={peer.__type}
        />
        <Grid w="100%" gutter="xs" grow>
          <Grid.Col className={classes.campaign_card_info_col_1}>
            <Text
              fz={{ base: 'xs', xs: 'sm' }}
              fw={600}
              className={classes.campaign_card_title}
              lineClamp={1}
            >
              {dataFields.name}
            </Text>
          </Grid.Col>
          <Grid.Col className={classes.campaign_card_info_col_2}>
            <Flex
              w="100%"
              //gap={{ base: 'xs', sm: 'md' }}
              className={cx(classes.campaign_card_stats, {
                [classes['campaign_card_stats--withAmountRaised']]: shouldShowAmountRaised,
              })}
              wrap={wrapDescription ? 'wrap' : 'nowrap'}
            >
              {showCampaignName && (
                <Text
                  size="xs"
                  className={classes.campaign_card_participant_team_name}
                  lineClamp={1}
                  w="fit-content"
                >
                  {campaign?.name}
                </Text>
              )}
              {showCampaignTimeLeft && typeof daysLeft === 'number' && (
                <Flex gap="xs" align="center" miw="125">
                  <IconClock size="16px" color="#AAAAAA" />
                  <Text size="xs" className={classes.campaign_card_participant_team_name}>
                    {daysLeft} days left
                  </Text>
                </Flex>
              )}
              {'participants_count' in peer ? (
                <TeamParticipants number={peer?.participants_count} />
              ) : peer?.team?.name && showTeamName ? (
                <Text size="xs" className={classes.campaign_card_participant_team_name}>
                  {peer?.team?.name}
                </Text>
              ) : null}
              {shouldShowGoal && isPeerGoalVisible ? (
                <AthonGoalProgress
                  activity={peer?.activity}
                  goal={Number(peer?.goal)}
                  campaign={campaign}
                  peerType={peer.__type}
                />
              ) : (
                shouldShowBackers && <Backers number={peer?.number_of_donations} />
              )}
            </Flex>
          </Grid.Col>
          {shouldShowAmountRaised && (
            <Grid.Col className={classes.campaign_card_info_col_3}>
              <Flex className={classes.campaign_card_amount_raised}>
                <CampaignAmountRaised amount={peer?.amount_raised} />
              </Flex>
            </Grid.Col>
          )}
        </Grid>
      </Flex>
    </Card>
  );
}

export { CampaignPeerInfoCard, showPeerGoal };
